import { Fragment, useContext } from "react";
import { Menu, Transition } from "@headlessui/react";
import { Bars3Icon, BellIcon } from "@heroicons/react/24/outline";
import AuthContext from "../AuthContext";
import { Link, useNavigate } from "react-router-dom";
import profile from "../assets/profile/user.png";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Header({ onMenuToggle }) {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const localStorageData = JSON.parse(localStorage.getItem("user"));

  const handleSignOut = () => {
    authContext.signout();
    navigate("/login", { replace: true });
  };

  return (
    <nav className="h-16 bg-slate-900 border-b border-slate-800 shadow-lg flex items-center px-4 sm:px-6 justify-between">
      {/* Left: hamburger (mobile) + brand (mobile only) */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onMenuToggle}
          className="lg:hidden rounded-lg p-2 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors duration-200 focus:outline-none"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>

        {/* Brand shown on mobile since sidebar is hidden */}
        <div className="flex items-center gap-2.5 lg:hidden">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 shadow-lg shadow-blue-600/40 shrink-0">
            <img
              src={require("../assets/janani_logo.jpg")}
              alt="logo"
              className="h-5 w-5 rounded object-cover"
            />
          </div>
          <div>
            <p className="text-sm font-bold text-white leading-tight">Janani Group</p>
            <p className="text-xs text-slate-400 hidden sm:block">IT Parts Inventory</p>
          </div>
        </div>
      </div>

      {/* Right: bell + profile */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="relative rounded-lg p-2 text-slate-400 hover:text-white hover:bg-slate-700/60 transition-colors duration-200 focus:outline-none"
        >
          <span className="sr-only">View notifications</span>
          <BellIcon className="h-5 w-5" aria-hidden="true" />
          <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-blue-500 ring-2 ring-slate-900" />
        </button>

        <div className="h-6 w-px bg-slate-700 mx-1" />

        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-700/60 transition-colors duration-200 focus:outline-none">
            <img
              className="h-7 w-7 rounded-full object-cover ring-2 ring-slate-600"
              src={profile}
              alt="profile"
            />
            <div className="text-left hidden lg:block">
              <p className="text-sm font-medium text-white leading-tight">
                {localStorageData?.name || "User"}
              </p>
              <p className="text-xs text-slate-400 leading-tight">
                {localStorageData?.role || "Admin"}
              </p>
            </div>
            <svg className="h-4 w-4 text-slate-400 hidden lg:block" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-30 mt-2 w-52 origin-top-right rounded-xl bg-white shadow-xl ring-1 ring-black/5 focus:outline-none overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">{localStorageData?.name}</p>
                <p className="text-xs text-gray-500 truncate">{localStorageData?.email}</p>
              </div>
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={classNames(
                        active ? "bg-gray-50 text-gray-900" : "text-gray-700",
                        "flex items-center gap-2 px-4 py-2 text-sm"
                      )}
                    >
                      <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profile
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleSignOut}
                      className={classNames(
                        active ? "bg-red-50 text-red-600" : "text-gray-700",
                        "flex w-full items-center gap-2 px-4 py-2 text-sm"
                      )}
                    >
                      <svg className="h-4 w-4 text-current opacity-70" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </nav>
  );
}
